import ClassNames from 'classnames';
import { get, map } from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';

import AspectRatio from 'components/AspectRatio';
import Carousel from 'components/Carousel';
import Theme from 'styles/theme';
import I18n from 'utils/I18n';

import Colorway from './Colorway';
import {
  COLORWAY_PICKER_ITEM_TYPES,
  COLORWAY_PICKER_APPEARANCES,
  DIRECTIONS,
  NAV_SIZES,
} from './constants';
import NavigationArrow from './NavigationArrow';

export { COLORWAY_PICKER_ITEM_TYPES, COLORWAY_PICKER_APPEARANCES, NAV_SIZES };

const ColorwayPicker = class extends React.Component {
  constructor(props) {
    super(props);

    this.containerRef = React.createRef();
  }

  get currentSlide() {
    const currentSlide = get(
      this.props.slides,
      [this.props.currentSlideIndex],
      [{ type: COLORWAY_PICKER_ITEM_TYPES.EMPTY }]
    );

    return currentSlide;
  }

  get currentSlideLength() {
    const currentSlideLength = get(this.currentSlide, ['length'], 1);

    return currentSlideLength;
  }

  // Returns true if first item of current slide is a "Previous Navigation Arrow"
  get showPreviousNavigationArrow() {
    return get(this.currentSlide, [0, 'type']) === COLORWAY_PICKER_ITEM_TYPES.NAVIGATION_ARROW_PREV;
  }

  // Returns true if last item of current slide is a "Next Navigation Arrow"
  get showNextNavigationArrow() {
    return (
      get(this.currentSlide, [this.currentSlideLength - 1, 'type']) ===
      COLORWAY_PICKER_ITEM_TYPES.NAVIGATION_ARROW_NEXT
    );
  }

  refocusContainer = () => {
    this.containerRef.current.focus();
  };

  handlePreviousNavigation = () => {
    this.props.onSlideIndexDecrement();
    this.refocusContainer();
  };

  handleNextNavigation = () => {
    this.props.onSlideIndexIncrement();
    this.refocusContainer();
  };

  render() {
    const {
      slides,
      currentSlideIndex,
      appearance,
      onSwatchSelect,
      activeColorwayHandle,
      diameter,
      navSize,
      className,
    } = this.props;

    return (
      <div
        className={ClassNames('ColorwayPicker', className, {
          'ColorwayPicker--color-swatches':
            appearance === COLORWAY_PICKER_APPEARANCES.COLOR_SWATCHES,
          'ColorwayPicker--thumbnails': appearance === COLORWAY_PICKER_APPEARANCES.THUMBNAILS,
        })}
      >
        <div className="ColorwayPicker__container" tabIndex="-1" ref={this.containerRef}>
          {/* The navigation arrows are rendered outside of the carousel so that they don't move when carousel is swiped */}
          {this.showPreviousNavigationArrow && (
            <div className="ColorwayPicker__item ColorwayPicker__item--nav ColorwayPicker__item--nav-prev">
              <AspectRatio width={1} height={1}>
                <div className="ColorwayPicker__item-content">
                  <NavigationArrow
                    direction={DIRECTIONS.LEFT}
                    appearance={appearance}
                    navSize={navSize}
                    onClick={this.handlePreviousNavigation}
                    aria-label={I18n.localize('pdp.carousel.viewPreviousImage')}
                  />
                </div>
              </AspectRatio>
            </div>
          )}

          <Carousel
            currentSlide={currentSlideIndex}
            options={{ draggable: false, loop: false, duration: 275, easing: 'ease-out' }}
            key={appearance}
          >
            {map(slides, (slide, slideIndex) => (
              <div
                className={ClassNames('ColorwayPicker__slide', {
                  'ColorwayPicker__slide--tabbable': slideIndex === currentSlideIndex,
                  'ColorwayPicker__slide--not-tabbable': slideIndex !== currentSlideIndex,
                })}
                key={slideIndex}
              >
                {map(slide, (item, itemIndex) => (
                  <div
                    className={`ColorwayPicker__item ColorwayPicker__item--${item.type}`}
                    key={`${slideIndex}--${itemIndex}`}
                  >
                    <AspectRatio width={1} height={1}>
                      <div className="ColorwayPicker__item-content">
                        {item.type === COLORWAY_PICKER_ITEM_TYPES.EMPTY && null}

                        {/* The navigation arrows are rendered outside of the carousel */}
                        {item.type === COLORWAY_PICKER_ITEM_TYPES.NAVIGATION_ARROW_PREV && null}
                        {item.type === COLORWAY_PICKER_ITEM_TYPES.NAVIGATION_ARROW_NEXT && null}

                        {item.type === COLORWAY_PICKER_ITEM_TYPES.COLORWAY && (
                          <Colorway
                            data={item.data}
                            appearance={appearance}
                            onSwatchSelect={onSwatchSelect}
                            activeColorwayHandle={activeColorwayHandle}
                            diameter={diameter}
                          />
                        )}
                      </div>
                    </AspectRatio>
                  </div>
                ))}
              </div>
            ))}
          </Carousel>

          {/* The navigation arrows are rendered outside of the carousel so that they don't move when carousel is swiped */}
          {this.showNextNavigationArrow && (
            <div className="ColorwayPicker__item ColorwayPicker__item--nav ColorwayPicker__item--nav-next">
              <AspectRatio width={1} height={1}>
                <div className="ColorwayPicker__item-content">
                  <NavigationArrow
                    direction={DIRECTIONS.RIGHT}
                    appearance={appearance}
                    navSize={navSize}
                    onClick={this.handleNextNavigation}
                    aria-label={I18n.localize('pdp.carousel.viewNextImage')}
                  />
                </div>
              </AspectRatio>
            </div>
          )}
        </div>
        <style jsx>{`
          .ColorwayPicker {
            width: 100%;
          }

          .ColorwayPicker--color-swatches {
            max-width: ${Theme.get('spacing') * 70}px;
          }

          .ColorwayPicker__container {
            position: relative;
            width: 100%;
            outline: none;
          }

          .ColorwayPicker__slide {
            display: flex;
          }

          .ColorwayPicker__slide--not-tabbable {
            visibility: hidden;
          }

          .ColorwayPicker__item {
            padding: ${Theme.get('spacing') / 2}px;
            width: ${100 / this.currentSlideLength}%;
          }

          .ColorwayPicker__item--nav {
            position: absolute;
            top: 0;
            z-index: 1;
            height: 100%;
            background-color: ${Theme.get('colors-white')};
            animation: fadeIn ease-in-out ${Theme.get('transition-duration') / 2}ms;
          }

          .ColorwayPicker__item--nav-prev {
            left: 0;
          }

          .ColorwayPicker__item--nav-next {
            right: 0;
          }

          .ColorwayPicker__item-content {
            width: 100%;
            height: 100%;
            display: flex;
            justify-content: center;
            align-items: center;
          }

          @keyframes fadeIn {
            from {
              opacity: 0;
            }
            to {
              opacity: 1;
            }
          }
        `}</style>
        <style jsx global>{`
          .ColorwayPicker .Carousel {
            width: 100%;
          }
        `}</style>
      </div>
    );
  }
};

ColorwayPicker.propTypes = {
  appearance: PropTypes.oneOf(Object.values(COLORWAY_PICKER_APPEARANCES)),
  slides: PropTypes.arrayOf(
    PropTypes.arrayOf(
      PropTypes.shape({
        type: PropTypes.oneOf(Object.values(COLORWAY_PICKER_ITEM_TYPES)).isRequired,
        data: PropTypes.object,
      })
    )
  ).isRequired,
  className: PropTypes.string,
  onSlideIndexIncrement: PropTypes.func,
  onSlideIndexDecrement: PropTypes.func,
  onSwatchSelect: PropTypes.func,
  currentSlideIndex: PropTypes.number,
  diameter: PropTypes.number,
  navSize: PropTypes.oneOf(Object.values(NAV_SIZES)),
  activeColorwayHandle: PropTypes.string,
};

ColorwayPicker.defaultProps = {
  appearance: COLORWAY_PICKER_APPEARANCES.COLOR_SWATCHES,
  className: null,
  onSlideIndexIncrement: () => {},
  onSlideIndexDecrement: () => {},
  onSwatchSelect: () => {},
  currentSlideIndex: 0,
  diameter: Theme.get('spacing') * 4,
  navSize: NAV_SIZES.MEDIUM,
  activeColorwayHandle: null,
};

export default ColorwayPicker;
